import type { AdminOrderDetailDto } from '@carcheck/shared';
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { OrderPiiValue } from './OrderPiiValue';
import { OrderStatusBadge } from './OrderStatusBadge';
import { tokens } from '../../../design';
import { shortId } from '../_lib/displayName';

/**
 * Order detail-drawer header (AUT-123): avatar (order number) + title +
 * customer name + status badge + short id + close button. Extracted from
 * the drawer to keep the body under the line cap. Mirrors the users
 * `UserDetailHeader`.
 */
export const OrderDetailHeader = ({
  order,
  onClose,
}: {
  order: AdminOrderDetailDto;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        p: '18px 24px',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Avatar
        variant="rounded"
        sx={{ width: 52, height: 52, fontSize: 14, fontWeight: 700, fontFamily: 'monospace' }}
      >
        {order.orderNumber}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }} noWrap>
          {t('orders.detail.title', { number: `№ ${order.orderNumber}` })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
          <OrderPiiValue value={order.customer.name} revealed={order.piiRevealed} />
          <OrderStatusBadge status={order.status} />
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: tokens.text.tertiary }}>
            {shortId(order.id)}
          </Typography>
        </Box>
      </Box>
      <IconButton onClick={onClose} sx={{ ml: 'auto' }} aria-label={t('orders.detail.close')}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
